import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { GoogleDriveLogo, ArrowClockwise, CheckCircle, WarningCircle, XCircle, Files } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

function StatusPill({ status }) {
  const map = {
    connected: { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: <CheckCircle size={12} weight="fill" />, label: "Connected" },
    not_connected: { cls: "bg-gray-100 text-gray-700 border-gray-200", icon: <XCircle size={12} weight="fill" />, label: "Not connected" },
    auth_failed: { cls: "bg-red-100 text-red-800 border-red-200", icon: <WarningCircle size={12} weight="fill" />, label: "Authentication failed" },
    checking: { cls: "bg-amber-100 text-amber-900 border-amber-200", icon: <ArrowClockwise size={12} weight="bold" />, label: "Checking…" },
    unknown: { cls: "bg-gray-100 text-gray-700 border-gray-200", icon: <XCircle size={12} weight="fill" />, label: "Unknown" },
  };
  const v = map[status] || map.unknown;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border ${v.cls}`}>
      {v.icon}
      {v.label}
    </span>
  );
}

export default function DriveStatusCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await api.get("/drive/status"); setData(r.data); }
    catch (e) { toast.error("Could not load Drive status"); }
  };
  useEffect(() => { load(); }, []);

  const sync = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/drive/sync");
      toast.success(`Sync complete — ${r.files_indexed} indexed of ${r.files_discovered} discovered`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
      load();
    } finally { setBusy(false); }
  };

  if (!data) return (
    <div className="p-5 rounded-2xl border border-line bg-surface/40 mb-6 text-ink-soft text-sm">Loading Google Drive status…</div>
  );

  return (
    <section className="p-5 rounded-2xl border border-line bg-surface/40 mb-6" data-testid="drive-status">
      <div className="flex items-start gap-3 mb-4">
        <GoogleDriveLogo size={22} weight="duotone" className="text-gk-charcoal mt-0.5" />
        <div className="flex-1">
          <div className="flex items-center flex-wrap gap-2">
            <h2 className="font-heading text-2xl text-gk-charcoal">Google Drive knowledge source</h2>
            <StatusPill status={data.connection_status} />
          </div>
          <p className="text-sm text-ink-soft mt-1">
            {data.configured
              ? "Tracey's training folder is connected. New, changed, and removed files are indexed automatically."
              : "Not yet activated. Once a service account is granted read access to the designated training folder, this section will show sync status."}
          </p>
        </div>
        <Button variant="outline" onClick={sync} disabled={busy} data-testid="drive-sync-now" className="rounded-full">
          <ArrowClockwise size={14} weight="bold" className={`mr-1.5 ${busy ? "animate-spin" : ""}`} />
          {busy ? "Syncing…" : "Sync now"}
        </Button>
      </div>

      {data.connection_error && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm flex items-start gap-2">
          <WarningCircle size={16} weight="fill" className="mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">Connection problem</div>
            <div className="text-xs mt-0.5">{data.connection_error}</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Stat label="Folder ID" value={data.folder_id || "—"} mono />
        <Stat label="Folder name" value={data.folder_name || "—"} />
        <Stat label="Files discovered" value={data.files_discovered} icon={<Files size={14} weight="duotone" />} />
        <Stat label="Files indexed" value={data.files_indexed} />
        <Stat label="Unsupported" value={data.unsupported} />
        <Stat label="Errors" value={data.errors_count} />
        <Stat label="Last sync started" value={fmt(data.last_sync_started_at)} />
        <Stat label="Last sync finished" value={fmt(data.last_sync_finished_at)} />
      </div>

      <div className="text-[11px] text-ink-soft space-y-1">
        <div>Service account: <span className="font-mono">{data.service_account_email || "not configured"}</span></div>
        <div>Auto-sync: {data.auto_sync_interval_minutes > 0 ? `every ${data.auto_sync_interval_minutes} min` : "disabled (manual sync only)"}</div>
        <div>Ask Tracey has <span className="font-medium">read-only</span> access. Originals in Drive are never modified, moved, renamed, or deleted from this app.</div>
      </div>

      {data.recent_errors?.length > 0 && (
        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-[0.18em] text-ink-soft mb-1">Recent errors</div>
          <ul className="text-xs text-red-800 space-y-1 list-disc pl-5">
            {data.recent_errors.map((e, i) => (<li key={i}>{e}</li>))}
          </ul>
        </div>
      )}
    </section>
  );
}

function Stat({ label, value, mono, icon }) {
  return (
    <div className="p-3 rounded-xl bg-white border border-line">
      <div className="text-[10px] uppercase tracking-[0.15em] text-ink-soft flex items-center gap-1">{icon}{label}</div>
      <div className={`text-sm text-gk-charcoal mt-1 truncate ${mono ? "font-mono text-[12px]" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}
